// components/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';

// Components (same as ProductView)
import FlashBar from "../components/FlashBar";
import ShareableNav from "../components/ShareableNav";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";      // For cart modal in Nav
import { CreditCard, Smartphone, Lock, Check, MapPin, User, ArrowLeft, Search, ChevronDown, ChevronUp, MessageCircle, Mail, Phone, Book, ShoppingBag, Truck, RotateCcw, HelpCircle, FileText, Users } from 'lucide-react';

const CACHE_KEY = 'checkout_page_cache';
const CACHE_TTL = 24*60*60 * 1000; // 1 minute

const CheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Layout configs – start null, filled from cache instantly
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [checkoutConfig, setCheckoutConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [cartItems, setCartItems] = useState([]); 
  const[categories,setCategories]=useState([]);
const[faqs,setFaqs]=useState([]);

  const defaultConfig = {
    activeDesign: 'side-by-side', // 'side-by-side', 'wizard', 'compact'
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = { ...defaultConfig, ...checkoutConfig };
  const {
    activeDesign,
    title,
    shippingTitle,
    paymentTitle,
    reviewTitle,
    emptyCartMessage,
    continueShoppingText,
    checkoutText,
    customStyles,
  } = finalConfig;

  // ────────────────────────────────────────────────
  // Cache Helpers
  // ────────────────────────────────────────────────
  const getCached = () => {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) return null;
    try {
      const { data, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp < CACHE_TTL) return data;
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCached = (data) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  };

 

 

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // Safe config handling
  const actualCheckoutConfig = 
    checkoutConfig?.checkoutSettings && typeof checkoutConfig.checkoutSettings === 'object' && !Array.isArray(checkoutConfig.checkoutSettings)
      ? checkoutConfig.checkoutSettings
      : (typeof checkoutConfig === 'object' && !Array.isArray(checkoutConfig) ? checkoutConfig : {});

  const checkoutDefault = {
    activeDesign: 'side-by-side',
    devMode: false,
    customStyles: {},
  };

  const finalCheckoutConfig = { ...checkoutDefault, ...actualCheckoutConfig };
  const { activeDesign: checkoutDesign, devMode: checkoutDevMode, customStyles: checkoutStyles } = finalCheckoutConfig;

  const containerStyles = {
    background: checkoutDevMode && checkoutStyles.background ? checkoutStyles.background : undefined,
    color: checkoutDevMode && checkoutStyles.textColor ? checkoutStyles.textColor : undefined,
  };

  // ── Mock Cart & Form Data ──────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    country: 'Kenya',
    mpesaPhone: '',
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCvv: '',
  });

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  const shipping = subtotal > 100 ? 0 : 10;
  const tax = subtotal * 0.16;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Order placed via ${paymentMethod === 'mpesa' ? 'M-Pesa' : 'Card'}!`);
  };


    const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

// Fetch ALL available FAQs for modal
  const fetchAllFaqs = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/pullHelp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data.categories || []);
      setFaqs(response.data.topics || []);
    } catch (err) {
      console.error("Failed to fetch all FAQs:", err);
    }
  };

  useEffect(() => {
    if (token) fetchAllFaqs();
  }, [token]);
  // Help Categories
 


  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  // Design 1: Side-by-Side Layout
  const Design1 = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">How can we help you?</h1>
          <p className="text-xl text-gray-300 mb-8">
            Search our knowledge base or browse categories below
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search knowledge base..."
              className="w-full pl-14 pr-4 py-4 rounded-xl text-gray-900 text-lg focus:ring-4 focus:ring-white/20 focus:outline-none"
            />
          </div>

          {searchQuery && (
            <p className="mt-4 text-gray-300">
              Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          )}
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-12 mb-12">
      

       
        {selectedCategory !== 'all' && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setSelectedCategory('all')}
              className="text-gray-900 font-medium hover:underline"
            >
              ← View all categories
            </button>
          </div>
        )}
      </div>

      {/* FAQs Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {selectedCategory === 'all' ? '' : categories.find(c => c.id === selectedCategory)?.name}
          </h2>
          <p className="text-gray-600">
            {filteredFaqs.length} topic{filteredFaqs.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {filteredFaqs.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search or browse our categories
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFaqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {expandedFaq === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  )}
                </button>
                
                {expandedFaq === faq.id && (
                  <div className="px-6 pb-5 text-gray-700 leading-relaxed animate-in fade-in slide-in-from-top-2">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Support Section */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Still need help?</h2>
            <p className="text-gray-600">Our support team is here to assist you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Live Chat */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Live Chat</h3>
              <p className="text-blue-100 text-sm mb-4">Chat with our team now</p>
              <button className="w-full bg-white text-blue-600 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                Start Chat
              </button>
              <p className="text-xs text-blue-100 mt-3">Avg. response: 2 min</p>
            </div>

            {/* Email 
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-gray-900 transition-colors">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 text-sm mb-4">Send us a message</p>
              <button className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                Send Email
              </button>
              <p className="text-xs text-gray-600 mt-3">Response within 24h</p>
            </div>
            */}

            {/* Phone */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-gray-900 transition-colors">
              <Phone className="w-12 h-12 mx-auto mb-4 text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 text-sm mb-4">Speak to our team</p>
              <button className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                +254 712 345 678
              </button>
              <p className="text-xs text-gray-600 mt-3">Mon-Fri, 8am-6pm</p>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );

  // Design 2: Multi-Step Wizard (Guided Flow)
  const Design2 = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex justify-between mb-12 max-w-md mx-auto">
          {['Shipping', 'Payment', 'Review'].map((label, index) => (
            <div key={label} className="flex-1 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto font-bold text-lg transition-all ${
                  step > index + 1
                    ? 'bg-green-600 text-white'
                    : step === index + 1
                    ? 'bg-gray-900 text-white ring-4 ring-gray-200'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step > index + 1 ? <Check size={24} /> : index + 1}
              </div>
              <p className={`mt-3 font-medium ${step === index + 1 ? 'text-gray-900' : 'text-gray-500'}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{shippingTitle}</h2>
              {/* Shipping form */}
              <button
                onClick={() => setStep(2)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Continue to Payment
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{paymentTitle}</h2>
              {/* Payment selection */}
              <button
                onClick={() => setStep(3)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Review & Confirm
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{reviewTitle}</h2>
              {/* Summary */}
              <button
                onClick={handleSubmit}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                {checkoutText} - ${total.toFixed(2)}
              </button>
            </>
          )}
        </div>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="mt-6 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}
      </div>
    </div>
  );

  // Design 3: Compact Mobile-First
  const Design3 = () => (
    <div className={`min-h-screen ${customStyles.background} py-8 px-4`}>
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">{title}</h2>

        {/* Compact shipping + payment + summary */}
        <div className="space-y-8">
          {/* Shipping */}
          <div>
            <h3 className="text-xl font-bold mb-4">{shippingTitle}</h3>
            {/* Compact form */}
          </div>

          {/* Payment */}
          <div>
            <h3 className="text-xl font-bold mb-4">{paymentTitle}</h3>
            {/* Compact payment */}
          </div>

          {/* Summary */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-bold mb-4">Summary</h3>
            <div className="flex justify-between font-bold text-xl">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
          >
            {checkoutText}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCheckoutDesign = () => {
    switch (checkoutDesign) {
      case 'side-by-side': return <Design1 />;
      case 'wizard': return <Design2 />;
      case 'compact': return <Design3 />;
      default: return <Design1 />;
    }
  };

 

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Flash Bar */}
    <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
     font-family: ${config.FONT_FAMILY}, sans-serif !important;
     }
      `}
      </style>
           <ShareableNav/>

      <br/><br/>

     
      
      <main className="flex-grow" style={containerStyles}>
      <br/><br/><br/><br/><br/>
<br/>

        {renderCheckoutDesign()}
      </main>
      

      {/* Footer */}
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default CheckoutPage;